// ---------------------------------------------------------------------------
// Pantalla de la integración con Rappi.
//
// Todo lo que escribe pasa por RPCs (rp_update_config, rp_agregar_local,
// rp_link_item, rp_aceptar_pedido, …): las tablas rp_* no aceptan escrituras
// desde el navegador. Configurar es de administradores; aceptar, rechazar,
// marcar listo y prender/apagar ítems lo puede hacer quien atiende.
//
// Mismo patrón que IntegracionMercadoLibre.jsx. El nombre no empieza con
// "Tienda": ese glob está PROTECTED en el sync y no viajaría a Gastify.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import {
  RP_ORDER_STATUS_LABELS,
  RP_STORE_STATUS_LABELS,
  RP_REJECT_MOTIVOS,
} from '../utils/rappi';
import { sonarAvisoRappi } from '../utils/rappiSonido';

const BTN = 'px-4 py-2 rounded-xl bg-slate-900 text-white text-xs';
const BTN_SEC = 'px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs';
const INPUT = 'w-full border border-slate-200 rounded-lg px-3 py-2 bg-white text-sm';
const EN_CURSO = ['recibido', 'aceptando', 'rechazando', 'aceptado_sin_venta', 'conflicto', 'error'];
const FILTROS = [
  { id: 'todos', label: 'Todos' },
  { id: 'sin_vincular', label: 'Sin vincular' },
  { id: 'apagados', label: 'Apagados' },
  { id: 'problemas', label: 'Con problemas' },
];

function SelectorProducto({ orgId, onElegir, disabled }) {
  const [texto, setTexto] = useState('');
  const [opciones, setOpciones] = useState([]);

  useEffect(() => {
    const q = texto.trim();
    if (q.length < 2) return undefined;
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from('products')
        .select('id, name, barcode')
        .eq('organization_id', orgId)
        .is('archived_at', null)
        .ilike('name', `%${q.replace(/[%_]/g, '')}%`)
        .order('name')
        .limit(15);
      setOpciones(data || []);
    }, 250);
    return () => clearTimeout(t);
  }, [texto, orgId]);

  return (
    <div className="relative">
      <input
        className="w-full border border-slate-200 rounded-lg px-2 py-1 text-xs"
        placeholder="Buscar producto del ERP…"
        value={texto}
        disabled={disabled}
        onChange={(e) => {
          setTexto(e.target.value);
          if (e.target.value.trim().length < 2) setOpciones([]);
        }}
      />
      {opciones.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-sm max-h-56 overflow-auto">
          {opciones.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="w-full text-left px-2 py-1 text-xs hover:bg-slate-50"
                onClick={() => {
                  setTexto('');
                  setOpciones([]);
                  onElegir(p);
                }}
              >
                {p.name}
                {p.barcode ? <span className="text-slate-400"> · {p.barcode}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CuentaRegresiva({ hasta }) {
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const resto = Math.floor((new Date(hasta).getTime() - ahora) / 1000);
  if (!Number.isFinite(resto)) return null;
  if (resto <= 0) return <span className="text-red-700 font-semibold">Plazo vencido</span>;
  const m = Math.floor(resto / 60);
  const s = String(resto % 60).padStart(2, '0');
  return <span className={resto < 90 ? 'text-red-700 font-semibold' : 'text-amber-700 font-semibold'}>{m}:{s}</span>;
}

function resumenPedido(o) {
  const lineas = Array.isArray(o?.normalized?.lines) ? o.normalized.lines : [];
  return lineas.map((l) => {
    const tops = (l.toppings || []).map((t) => t.name).filter(Boolean);
    return `${l.quantity}× ${l.name || l.sku || 'Producto'}${tops.length ? ` (${tops.join(', ')})` : ''}`;
  });
}

function TarjetaPedido({ o, local, onAccion, ocupado, readyManual }) {
  const [coccion, setCoccion] = useState(o.cooking_time || 15);
  const [motivo, setMotivo] = useState(RP_REJECT_MOTIVOS[0].id);
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);
  const esperando = o.status === 'recibido';
  const vencido = Boolean(esperando && o.accept_deadline_at && new Date(o.accept_deadline_at).getTime() < ahora);
  const lineas = resumenPedido(o);
  const color = esperando ? 'border-amber-300 bg-amber-50/40'
    : ['aceptado_sin_venta', 'conflicto', 'error'].includes(o.status) ? 'border-red-200 bg-red-50/40'
      : 'border-slate-200';

  return (
    <li className={`border rounded-xl p-4 space-y-2 ${color}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-slate-800">
            #{o.rappi_order_id}
            {o.customer_name ? <span className="font-normal text-slate-500"> · {o.customer_name}</span> : null}
          </div>
          <div className="text-xs text-slate-500">
            {local?.nombre || local?.rappi_store_id || 'Local'}
            {o.delivery_method ? ` · ${o.delivery_method}` : ''}
            {o.payment_method ? ` · ${o.payment_method}` : ''}
            {o.total_amount != null ? ` · $${Number(o.total_amount).toLocaleString('es-AR')}` : ''}
          </div>
        </div>
        <div className="text-right text-sm">
          <div className="text-slate-500 text-xs">{RP_ORDER_STATUS_LABELS[o.status] || o.status}</div>
          {esperando && o.accept_deadline_at && <CuentaRegresiva hasta={o.accept_deadline_at} />}
        </div>
      </div>

      {lineas.length > 0 && (
        <ul className="text-sm text-slate-700 list-disc pl-5">
          {lineas.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      )}
      {o.problemas && <div className="text-sm text-red-700">{o.problemas}</div>}

      {esperando && (o.auto_bloqueado || vencido) && (
        <div className="flex gap-2 flex-wrap items-center pt-1">
          <span className="text-xs text-slate-500">
            {vencido ? 'Se venció el plazo. ' : ''}Si ya figura aceptado en el portal o la tablet de Rappi:
          </span>
          <Button className={BTN_SEC} isLoading={ocupado === `y-${o.id}`}
            onClick={() => onAccion(`y-${o.id}`, 'rp_marcar_aceptado_manual', { p_order: o.id }, 'Registrando la venta…')}>
            Ya está aceptado en Rappi
          </Button>
        </div>
      )}

      {esperando && !vencido && (
        <div className="flex gap-2 flex-wrap items-end pt-1">
          <label className="text-xs text-slate-500">
            Minutos de preparación
            <input type="number" min="1" max="120" value={coccion}
              onChange={(e) => setCoccion(e.target.value)}
              className="block w-20 border border-slate-200 rounded-lg px-2 py-1 text-sm" />
          </label>
          <Button className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs"
            isLoading={ocupado === `a-${o.id}`}
            onClick={() => onAccion(`a-${o.id}`, 'rp_aceptar_pedido',
              { p_order: o.id, p_cooking_time: Math.max(1, Math.min(120, Math.floor(Number(coccion) || 15))) },
              'Aceptando en Rappi…')}>
            Aceptar
          </Button>
          <select className="border border-slate-200 rounded-lg px-2 py-2 text-xs bg-white" value={motivo}
            onChange={(e) => setMotivo(e.target.value)}>
            {RP_REJECT_MOTIVOS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <Button className={BTN_SEC} isLoading={ocupado === `r-${o.id}`}
            onClick={() => {
              if (!window.confirm('Rechazar un pedido baja la reputación del local en Rappi. ¿Rechazarlo igual?')) return;
              onAccion(`r-${o.id}`, 'rp_rechazar_pedido', { p_order: o.id, p_motivo: motivo }, 'Rechazando en Rappi…');
            }}>
            Rechazar
          </Button>
        </div>
      )}

      {readyManual && ['venta_creada', 'aceptado_sin_venta'].includes(o.status) && !o.ready_at && (
        <Button className={BTN_SEC} isLoading={ocupado === `l-${o.id}`}
          onClick={() => onAccion(`l-${o.id}`, 'rp_marcar_listo', { p_order: o.id }, 'Avisando a Rappi…')}>
          Listo para retirar
        </Button>
      )}
    </li>
  );
}

function IntegracionRappi() {
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  const esAdmin = ['admin', 'administrador'].includes(String(profile?.role || '').trim().toLowerCase());

  const [conexion, setConexion] = useState(null);
  const [locales, setLocales] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [recientes, setRecientes] = useState([]);
  const [items, setItems] = useState([]);
  const [productos, setProductos] = useState({});
  const [logs, setLogs] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [medios, setMedios] = useState([]);
  const [form, setForm] = useState({
    is_active: true, accept_mode: 'auto', default_cooking_time: 15, ready_mode: 'auto',
    payment_method_id: '', payment_method_cash_id: '', sync_availability: false, default_stock_buffer: 0,
  });
  const [nuevoLocal, setNuevoLocal] = useState({ rappi_store_id: '', site_id: '' });
  const [localMenu, setLocalMenu] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [ocupado, setOcupado] = useState('');
  const vistos = useRef(null);

  const fetchPedidos = useCallback(async () => {
    if (!orgId) return;
    const [enCurso, rec] = await Promise.all([
      supabase.from('rp_orders')
        .select('id, store_id, rappi_order_id, status, problemas, delivery_method, payment_method, cooking_time, accept_deadline_at, customer_name, total_amount, normalized, sale_id, ready_at, auto_bloqueado, created_at')
        .eq('organization_id', orgId)
        .in('status', EN_CURSO)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase.from('rp_orders')
        .select('id, store_id, rappi_order_id, status, problemas, delivery_method, payment_method, total_amount, normalized, sale_id, ready_at, customer_name, created_at')
        .eq('organization_id', orgId)
        .eq('status', 'venta_creada')
        .gte('created_at', new Date(Date.now() - 12 * 3600 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(20),
    ]);
    const lista = enCurso.data || [];
    setPedidos(lista);
    setRecientes(rec.data || []);

    // Aviso sonoro de pedidos nuevos esperando aceptación.
    const esperando = lista.filter((o) => o.status === 'recibido').map((o) => o.id);
    if (vistos.current === null) {
      vistos.current = new Set(esperando);
    } else {
      const nuevos = esperando.filter((id) => !vistos.current.has(id));
      nuevos.forEach((id) => vistos.current.add(id));
      if (nuevos.length) sonarAvisoRappi();
    }
  }, [orgId]);

  const fetchEstado = useCallback(async () => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    const [conRes, locRes, logRes, sitRes, pmRes] = await Promise.all([
      supabase.from('rp_connections').select('*').eq('organization_id', orgId).maybeSingle(),
      supabase.from('rp_stores').select('*').eq('organization_id', orgId).order('created_at'),
      supabase.from('rp_sync_log').select('id, level, event, message, created_at')
        .eq('organization_id', orgId).order('created_at', { ascending: false }).limit(25),
      supabase.from('sitios').select('id, nombre').eq('organization_id', orgId).eq('is_active', true).order('nombre'),
      supabase.from('payment_methods').select('id, name')
        .eq('organization_id', orgId).eq('is_active', true).is('archived_at', null).order('name'),
    ]);
    const c = conRes.data || null;
    const locs = locRes.data || [];
    setConexion(c);
    setLocales(locs);
    setLogs(logRes.data || []);
    setSitios(sitRes.data || []);
    setMedios(pmRes.data || []);
    if (c) {
      setForm({
        is_active: c.is_active !== false,
        accept_mode: c.accept_mode || 'auto',
        default_cooking_time: c.default_cooking_time || 15,
        ready_mode: c.ready_mode || 'auto',
        payment_method_id: c.payment_method_id || '',
        payment_method_cash_id: c.payment_method_cash_id || '',
        sync_availability: c.sync_availability === true,
        default_stock_buffer: Number(c.default_stock_buffer || 0),
      });
    }
    setLocalMenu((prev) => prev || locs[0]?.id || '');
    await fetchPedidos();
    setLoading(false);
  }, [orgId, fetchPedidos]);

  const fetchItems = useCallback(async () => {
    if (!localMenu) {
      setItems([]);
      return;
    }
    const todos = [];
    for (let desde = 0; desde < 10000; desde += 1000) {
      const { data, error } = await supabase.from('rp_items')
        .select('id, rappi_sku, kind, name, price, product_id, link_source, sync_availability, stock_buffer, manual_state, last_sent_state, last_sent_at, sync_state, last_error')
        .eq('store_id', localMenu)
        .order('kind')
        .order('name')
        .order('id')
        .range(desde, desde + 999);
      if (error) break;
      todos.push(...(data || []));
      if (!data || data.length < 1000) break;
    }
    setItems(todos);
    const ids = [...new Set(todos.map((i) => i.product_id).filter(Boolean))];
    const mapa = {};
    for (let i = 0; i < ids.length; i += 200) {
      const { data: prods } = await supabase.from('products').select('id, name').in('id', ids.slice(i, i + 200));
      (prods || []).forEach((p) => { mapa[p.id] = p.name; });
    }
    setProductos(mapa);
  }, [localMenu]);

  useEffect(() => { fetchEstado(); }, [fetchEstado]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  // La bandeja se refresca sola: el plazo para aceptar es de minutos.
  useEffect(() => {
    if (!locales.length) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') fetchPedidos();
    }, 8000);
    return () => clearInterval(t);
  }, [locales.length, fetchPedidos]);

  const rpc = async (clave, nombre, params, exito, { recargarItems = false } = {}) => {
    setOcupado(clave);
    const { data, error } = await supabase.rpc(nombre, params);
    setOcupado('');
    if (error) {
      toast.error(error.message);
      return null;
    }
    if (exito) toast.success(typeof exito === 'function' ? exito(data) : exito);
    await fetchEstado();
    if (recargarItems) await fetchItems();
    return data ?? true;
  };

  const handleGuardar = () => rpc('guardar', 'rp_update_config', {
    p_is_active: form.is_active,
    p_accept_mode: form.accept_mode,
    p_default_cooking_time: Math.max(1, Math.min(120, Math.floor(Number(form.default_cooking_time) || 15))),
    p_ready_mode: form.ready_mode,
    p_payment_method_id: form.payment_method_id || null,
    p_payment_method_cash_id: form.payment_method_cash_id || null,
    p_sync_availability: form.sync_availability,
    p_default_stock_buffer: Math.max(0, Math.floor(Number(form.default_stock_buffer) || 0)),
  }, 'Configuración guardada');

  const handleAgregarLocal = async () => {
    const res = await rpc('agregar', 'rp_agregar_local', {
      p_rappi_store_id: nuevoLocal.rappi_store_id.trim(),
      p_site_id: nuevoLocal.site_id || null,
    });
    if (res?.codigo_vinculacion) {
      toast.success(`Local agregado. Pedile a Rappi que cargue ${res.codigo_vinculacion} como ID externo.`, { duration: 8000 });
      setNuevoLocal({ rappi_store_id: '', site_id: '' });
    }
  };

  // Productos con stock por talle/color: el vínculo tiene que ir a una
  // variante (si no, la venta no puede descontar stock).
  const handleVincular = async (item, producto) => {
    const { data: prod } = await supabase.from('products').select('stock_tracking').eq('id', producto.id).maybeSingle();
    let variante = null;
    if (prod?.stock_tracking === 'variant') {
      const { data: variantes } = await supabase.from('product_variants')
        .select('id, sku, attributes').eq('product_id', producto.id).order('sku');
      const opciones = (variantes || []).map((v, idx) => {
        const attrs = v.attributes && typeof v.attributes === 'object' ? Object.values(v.attributes).join(' ') : '';
        return `${idx + 1}) ${[v.sku, attrs].filter(Boolean).join(' · ') || v.id}`;
      });
      const elegido = window.prompt(`${producto.name} lleva stock por talle/color. Elegí la variante:\n${opciones.join('\n')}`, '1');
      const n = Number(elegido);
      if (!Number.isInteger(n) || n < 1 || n > (variantes || []).length) {
        toast.error('No se vinculó: elegí una variante');
        return;
      }
      variante = variantes[n - 1].id;
    }
    await rpc(`i-${item.id}`, 'rp_link_item', { p_item: item.id, p_product: producto.id, p_variant: variante },
      `Vinculado a ${producto.name}`, { recargarItems: true });
  };

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success('Copiado');
    } catch {
      toast(texto);
    }
  };

  const localesPorId = useMemo(() => Object.fromEntries(locales.map((l) => [l.id, l])), [locales]);

  const itemsFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return items.filter((i) => {
      if (filtro === 'sin_vincular' && i.product_id) return false;
      if (filtro === 'apagados' && i.last_sent_state !== 'off' && i.manual_state !== 'off') return false;
      if (filtro === 'problemas' && i.sync_state !== 'error') return false;
      if (!q) return true;
      return [i.name, i.rappi_sku, productos[i.product_id]].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [items, filtro, busqueda, productos]);

  if (loading) return <div className="p-6 text-slate-500">Cargando…</div>;
  if (!orgId) return <div className="p-6 text-slate-500">No se encontró la empresa de tu usuario.</div>;

  const multiSitio = sitios.length > 1;
  const esperando = pedidos.filter((o) => o.status === 'recibido');
  const otrosEnCurso = pedidos.filter((o) => o.status !== 'recibido');
  const hayParaReintentar = pedidos.some((o) => ['aceptado_sin_venta', 'recibido'].includes(o.status));
  const readyManual = conexion?.ready_mode === 'manual';
  const localActual = localesPorId[localMenu];

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Rappi</h1>
        <p className="text-slate-500 mt-1">
          Los pedidos de Rappi se aceptan desde acá (solos o a mano) y entran como ventas del ERP: stock, libro diario y
          banco, listos para facturar. Si lo activás, los productos sin stock se apagan solos en Rappi.
        </p>
      </div>

      {/* Bandeja */}
      {locales.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="font-semibold text-slate-800">
              Pedidos
              {esperando.length > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs">
                  {esperando.length} esperando
                </span>
              )}
            </h2>
            {esAdmin && hayParaReintentar && (
              <Button className={BTN_SEC} isLoading={ocupado === 'retry'}
                onClick={() => rpc('retry', 'rp_request_action', { p_action: 'retry_orders', p_store: null },
                  (n) => (n ? `${n} pedido(s) reintentados` : 'No había pedidos para reintentar'))}>
                Reintentar
              </Button>
            )}
          </div>
          {pedidos.length === 0 && recientes.length === 0 && (
            <p className="text-sm text-slate-400">Sin pedidos pendientes. Esta bandeja se actualiza sola.</p>
          )}
          <ul className="space-y-3">
            {[...esperando, ...otrosEnCurso].map((o) => (
              <TarjetaPedido key={o.id} o={o} local={localesPorId[o.store_id]} ocupado={ocupado}
                readyManual={readyManual} onAccion={(clave, fn, params, msg) => rpc(clave, fn, params, msg)} />
            ))}
          </ul>
          {recientes.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-600 mb-2">Registrados en las últimas 12 horas</h3>
              <ul className="space-y-3">
                {recientes.map((o) => (
                  <TarjetaPedido key={o.id} o={o} local={localesPorId[o.store_id]} ocupado={ocupado}
                    readyManual={readyManual} onAccion={(clave, fn, params, msg) => rpc(clave, fn, params, msg)} />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Configuración */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-semibold text-slate-800">Configuración</h2>
          {conexion && (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${conexion.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
              {conexion.is_active ? 'Activa' : 'Pausada'}
            </span>
          )}
        </div>
        {!conexion && (
          <p className="text-sm text-slate-500">
            Rappi trabaja con integradores: tu ejecutivo de Rappi tiene que asociar tu local a Impulza. Guardá la
            configuración y después agregá el local.
          </p>
        )}
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <label className="space-y-1 block">
            <span className="text-slate-500">Aceptación de pedidos</span>
            <select className={INPUT} value={form.accept_mode} disabled={!esAdmin}
              onChange={(e) => setForm((f) => ({ ...f, accept_mode: e.target.value }))}>
              <option value="auto">Automática (si todo está vinculado y hay stock)</option>
              <option value="manual">Manual (siempre acepta una persona)</option>
            </select>
            <span className="block text-xs text-slate-400">
              En automático nunca se rechaza un pedido: si falta algo, queda esperando acá con alarma.
            </span>
          </label>
          <label className="space-y-1 block">
            <span className="text-slate-500">Minutos de preparación por defecto</span>
            <input type="number" min="1" max="120" className={INPUT} value={form.default_cooking_time} disabled={!esAdmin}
              onChange={(e) => setForm((f) => ({ ...f, default_cooking_time: e.target.value }))} />
          </label>
          <label className="space-y-1 block">
            <span className="text-slate-500">Medio de cobro de los pedidos</span>
            <select className={INPUT} value={form.payment_method_id} disabled={!esAdmin}
              onChange={(e) => setForm((f) => ({ ...f, payment_method_id: e.target.value }))}>
              <option value="">Elegí un medio</option>
              {medios.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <span className="block text-xs text-slate-400">
              Usá un medio con la comisión de Rappi y sus días de acreditación: el asiento va por el neto y no entra al arqueo.
            </span>
          </label>
          <label className="space-y-1 block">
            <span className="text-slate-500">Medio para efectivo con retiro en el local</span>
            <select className={INPUT} value={form.payment_method_cash_id} disabled={!esAdmin}
              onChange={(e) => setForm((f) => ({ ...f, payment_method_cash_id: e.target.value }))}>
              <option value="">Ninguno (esos pedidos esperan a mano)</option>
              {medios.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <span className="block text-xs text-slate-400">Esa plata entra al cajón: la venta sí va al arqueo.</span>
          </label>
          <label className="space-y-1 block">
            <span className="text-slate-500">Aviso de "listo para retirar"</span>
            <select className={INPUT} value={form.ready_mode} disabled={!esAdmin}
              onChange={(e) => setForm((f) => ({ ...f, ready_mode: e.target.value }))}>
              <option value="auto">Automático (lo calcula Rappi)</option>
              <option value="manual">Manual (botón en cada pedido)</option>
            </select>
          </label>
          <label className="space-y-1 block">
            <span className="text-slate-500">Colchón de stock para ítems nuevos</span>
            <input type="number" min="0" className={INPUT} value={form.default_stock_buffer} disabled={!esAdmin}
              onChange={(e) => setForm((f) => ({ ...f, default_stock_buffer: e.target.value }))} />
          </label>
          <label className="flex items-start gap-2 sm:col-span-2">
            <input type="checkbox" className="mt-1" checked={form.sync_availability} disabled={!esAdmin}
              onChange={(e) => setForm((f) => ({ ...f, sync_availability: e.target.checked }))} />
            <span>
              <span className="text-slate-700">Apagar en Rappi lo que se queda sin stock</span>
              <span className="block text-xs text-slate-400">
                Se apaga cuando el stock de la sucursal menos el colchón llega a cero, y se vuelve a prender cuando hay.
                Solo se prende lo que apagó el ERP: lo que apagues a mano en Rappi no se toca. Desactivalo por ítem en los
                platos que no llevan stock propio. Con esto activo, un pedido sin stock no se acepta solo.
              </span>
            </span>
          </label>
          <label className="flex items-center gap-2 sm:col-span-2">
            <input type="checkbox" checked={form.is_active} disabled={!esAdmin}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
            <span className="text-slate-700">Integración activa</span>
          </label>
        </div>
        {esAdmin ? (
          <Button className={BTN} onClick={handleGuardar} isLoading={ocupado === 'guardar'}>Guardar</Button>
        ) : (
          <p className="text-xs text-slate-400">Solo un administrador puede cambiar la configuración.</p>
        )}
      </div>

      {/* Locales */}
      {conexion && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
          <h2 className="font-semibold text-slate-800">Locales</h2>
          {locales.length === 0 && (
            <p className="text-sm text-slate-500">
              Cargá el ID del local tal como figura en el portal de aliados de Rappi. Te damos un código para que Rappi lo
              cargue como ID externo: así confirmamos que el local es tuyo.
            </p>
          )}
          <ul className="divide-y divide-slate-100">
            {locales.map((l) => (
              <li key={l.id} className="py-3 space-y-2 text-sm">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-medium text-slate-800">{l.nombre || `Local ${l.rappi_store_id}`}</div>
                    <div className="text-xs text-slate-500">
                      ID Rappi {l.rappi_store_id} · Código{' '}
                      <button type="button" className="font-mono underline" onClick={() => copiar(l.codigo_vinculacion)}>
                        {l.codigo_vinculacion}
                      </button>
                      {l.is_online === true && ' · online en Rappi'}
                      {l.is_online === false && ' · offline en Rappi'}
                    </div>
                    {l.status_detail && l.status !== 'activo' && <div className="text-xs text-amber-700 mt-1">{l.status_detail}</div>}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${l.status === 'activo' ? 'bg-emerald-50 text-emerald-700' : l.status === 'inactivo' ? 'bg-slate-100 text-slate-600' : 'bg-amber-50 text-amber-700'}`}>
                    {RP_STORE_STATUS_LABELS[l.status] || l.status}
                  </span>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  <select className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white" value={l.site_id || ''} disabled={!esAdmin}
                    onChange={(e) => rpc(`s-${l.id}`, 'rp_update_local', { p_store: l.id, p_site_id: e.target.value || null, p_is_active: null }, 'Sucursal guardada')}>
                    <option value="">{multiSitio ? 'Elegí la sucursal que descuenta stock' : 'Sin sucursal'}</option>
                    {sitios.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                  {esAdmin && l.status !== 'activo' && l.status !== 'inactivo' && (
                    <Button className={BTN_SEC} isLoading={ocupado === `v-${l.id}`}
                      onClick={() => rpc(`v-${l.id}`, 'rp_request_action', { p_action: 'validate', p_store: l.id }, 'Validando con Rappi…')}>
                      Volver a validar
                    </Button>
                  )}
                  {esAdmin && l.status === 'activo' && (
                    <Button className={BTN_SEC} isLoading={ocupado === `m-${l.id}`}
                      onClick={() => rpc(`m-${l.id}`, 'rp_request_action', { p_action: 'menu', p_store: l.id }, 'Importando el menú: en un minuto aparece en Actividad')}>
                      Importar menú
                    </Button>
                  )}
                  {esAdmin && (
                    <Button className={BTN_SEC} isLoading={ocupado === `p-${l.id}`}
                      onClick={() => rpc(`p-${l.id}`, 'rp_update_local', { p_store: l.id, p_site_id: l.site_id, p_is_active: l.status === 'inactivo' }, l.status === 'inactivo' ? 'Local reactivado' : 'Local pausado')}>
                      {l.status === 'inactivo' ? 'Reactivar' : 'Pausar'}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {esAdmin && (
            <div className="flex gap-2 flex-wrap items-end border-t border-slate-100 pt-4">
              <label className="text-xs text-slate-500">
                ID del local en Rappi
                <input className="block border border-slate-200 rounded-lg px-2 py-2 text-sm" value={nuevoLocal.rappi_store_id}
                  onChange={(e) => setNuevoLocal((n) => ({ ...n, rappi_store_id: e.target.value }))} />
              </label>
              <label className="text-xs text-slate-500">
                Sucursal
                <select className="block border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white" value={nuevoLocal.site_id}
                  onChange={(e) => setNuevoLocal((n) => ({ ...n, site_id: e.target.value }))}>
                  <option value="">{multiSitio ? 'Elegí una' : 'La única activa'}</option>
                  {sitios.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                </select>
              </label>
              <Button className={BTN} isLoading={ocupado === 'agregar'} disabled={!nuevoLocal.rappi_store_id.trim()}
                onClick={handleAgregarLocal}>
                Agregar local
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Menú */}
      {locales.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-semibold text-slate-800">Menú</h2>
            <div className="flex gap-2 flex-wrap items-center">
              {locales.length > 1 && (
                <select className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white" value={localMenu}
                  onChange={(e) => setLocalMenu(e.target.value)}>
                  {locales.map((l) => <option key={l.id} value={l.id}>{l.nombre || l.rappi_store_id}</option>)}
                </select>
              )}
              {FILTROS.map((f) => (
                <button key={f.id} type="button" onClick={() => setFiltro(f.id)}
                  className={`px-3 py-1 rounded-full text-xs border ${filtro === f.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}>
                  {f.label}
                </button>
              ))}
              <input className="border border-slate-200 rounded-lg px-3 py-1 text-xs" placeholder="Buscar…"
                value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Los ítems con el mismo SKU que un producto del ERP se vinculan solos al importar. Un pedido con un producto sin
            vincular no se acepta solo. Un adicional sin vincular suma su precio al producto y no mueve stock.
          </p>
          {items.length === 0 && (
            <p className="text-sm text-slate-400">
              {localActual?.status === 'activo'
                ? 'Todavía no se importó el menú de este local.'
                : 'El menú se importa cuando Rappi valida el local.'}
            </p>
          )}
          {items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-3">Ítem de Rappi</th>
                    <th className="py-2 pr-3">Producto del ERP</th>
                    <th className="py-2 pr-3 text-center">Por stock</th>
                    <th className="py-2 pr-3 text-center">Colchón</th>
                    <th className="py-2">En Rappi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {itemsFiltrados.slice(0, 300).map((i) => {
                    const oc = ocupado === `i-${i.id}`;
                    const estado = i.manual_state || i.last_sent_state;
                    return (
                      <tr key={i.id} className="align-top">
                        <td className="py-2 pr-3 min-w-[12rem]">
                          <div className="text-slate-700">{i.name || i.rappi_sku}</div>
                          <div className="text-slate-400">
                            {i.kind === 'topping' ? 'Adicional · ' : ''}SKU {i.rappi_sku}
                            {i.price != null ? ` · $${Number(i.price).toLocaleString('es-AR')}` : ''}
                          </div>
                        </td>
                        <td className="py-2 pr-3 min-w-[12rem]">
                          {i.product_id ? (
                            <div className="flex items-start gap-2">
                              <span className="text-slate-700">{productos[i.product_id] || 'Producto'}</span>
                              {esAdmin && (
                                <button type="button" className="text-slate-400 hover:text-red-600" disabled={oc}
                                  onClick={() => rpc(`i-${i.id}`, 'rp_unlink_item', { p_item: i.id }, 'Desvinculado', { recargarItems: true })}>
                                  ✕
                                </button>
                              )}
                            </div>
                          ) : (
                            <SelectorProducto orgId={orgId} disabled={!esAdmin || oc}
                              onElegir={(p) => handleVincular(i, p)} />
                          )}
                        </td>
                        <td className="py-2 pr-3 text-center">
                          <input type="checkbox" checked={i.sync_availability} disabled={!esAdmin || oc}
                            onChange={(e) => rpc(`i-${i.id}`, 'rp_update_item', { p_item: i.id, p_sync_availability: e.target.checked, p_stock_buffer: null }, null, { recargarItems: true })} />
                        </td>
                        <td className="py-2 pr-3 text-center">
                          <input type="number" min="0" className="w-14 border border-slate-200 rounded px-1 py-0.5 text-center"
                            defaultValue={i.stock_buffer} disabled={!esAdmin || oc}
                            onBlur={(e) => {
                              const v = Math.max(0, Math.floor(Number(e.target.value) || 0));
                              if (v !== Number(i.stock_buffer)) {
                                rpc(`i-${i.id}`, 'rp_update_item', { p_item: i.id, p_sync_availability: null, p_stock_buffer: v }, null, { recargarItems: true });
                              }
                            }} />
                        </td>
                        <td className="py-2 min-w-[11rem]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={estado === 'off' ? 'text-red-700' : estado === 'on' ? 'text-emerald-700' : 'text-slate-400'}>
                              {estado === 'off' ? 'Apagado' : estado === 'on' ? 'Prendido' : 'Sin cambios'}
                              {i.manual_state ? ' (a mano)' : ''}
                            </span>
                            {i.manual_state !== 'off' && (
                              <button type="button" className="underline text-slate-500" disabled={oc}
                                onClick={() => rpc(`i-${i.id}`, 'rp_set_item_manual', { p_item: i.id, p_state: 'off' }, 'Se apaga en Rappi', { recargarItems: true })}>
                                Apagar
                              </button>
                            )}
                            {i.manual_state !== 'on' && (
                              <button type="button" className="underline text-slate-500" disabled={oc}
                                onClick={() => rpc(`i-${i.id}`, 'rp_set_item_manual', { p_item: i.id, p_state: 'on' }, 'Se prende en Rappi', { recargarItems: true })}>
                                Prender
                              </button>
                            )}
                            {i.manual_state && (
                              <button type="button" className="underline text-slate-500" disabled={oc}
                                onClick={() => rpc(`i-${i.id}`, 'rp_set_item_manual', { p_item: i.id, p_state: null }, 'Vuelve a seguir el stock', { recargarItems: true })}>
                                Según stock
                              </button>
                            )}
                          </div>
                          {i.sync_state === 'error' && <div className="text-red-700">{i.last_error || 'Error'}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {itemsFiltrados.length > 300 && (
                <p className="text-xs text-slate-400 mt-2">Mostrando 300 de {itemsFiltrados.length}. Usá la búsqueda para encontrar el resto.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Actividad */}
      {conexion && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="font-semibold text-slate-800 mb-3">Actividad</h2>
          {logs.length === 0 && <p className="text-sm text-slate-400">Sin movimientos todavía.</p>}
          <ul className="divide-y divide-slate-100">
            {logs.map((log) => (
              <li key={log.id} className="py-2 flex items-start gap-3 text-sm">
                <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${log.level === 'error' ? 'bg-red-500' : log.level === 'warn' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-700 break-words">{log.message}</div>
                  <div className="text-xs text-slate-400">{log.event} · {new Date(log.created_at).toLocaleString('es-AR')}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default IntegracionRappi;
